// Local storage read-out (STO-1/3, M41). Count the bytes of persisted player-caused data
// per world — edited sections (RLE), block entities, entities, and the player row —
// scanned over the IndexedDB rows. Pristine chunks are free (ENG-10 regenerates them);
// tiles are the tile service's cost, never the user's. This is the local M41 read-out so
// the usage UX exists before the server quota does (M42, STO-2/4). No billing anywhere.
import type { SaveManager } from '../storage/saves';
import { STO } from '../../sim/config';

export interface WorldUsage {
  id: string;
  name: string;
  bytes: number;
}

export interface UsageReport {
  worlds: WorldUsage[];
  totalBytes: number;
  quotaBytes: number;
  warnFraction: number;
  /** total / quota (0 when the quota is 0). */
  fraction: number;
  /** total is at or past the warn threshold (STO_WARN_FRACTION). */
  warn: boolean;
}

const jsonBytes = (v: unknown): number => (v == null ? 0 : JSON.stringify(v).length);

/** Bytes of persisted player-caused data for one world (STO-1): section RLE + block
 *  entities + entities + the player row. */
export async function worldBytes(save: SaveManager, id: string): Promise<number> {
  let bytes = 0;
  for (const s of await save.sectionRows(id)) bytes += s.bytes; // RLE byte length
  for (const { list } of await save.allBlockEntities(id)) bytes += jsonBytes(list);
  for (const { list } of await save.allEntities(id)) bytes += jsonBytes(list);
  bytes += jsonBytes(await save.getPlayer(id));
  return bytes;
}

/** The whole-save usage read-out (STO-3): per-world bytes (largest first), the total, the
 *  quota, and whether the total has crossed the warn threshold. */
export async function computeUsage(save: SaveManager, quotaBytes: number = STO.DEFAULT_QUOTA_BYTES): Promise<UsageReport> {
  const worlds = await save.listWorlds();
  const rows: WorldUsage[] = [];
  let total = 0;
  for (const w of worlds) {
    const bytes = await worldBytes(save, w.id);
    rows.push({ id: w.id, name: w.name, bytes });
    total += bytes;
  }
  rows.sort((a, b) => b.bytes - a.bytes);
  const fraction = quotaBytes > 0 ? total / quotaBytes : 0;
  return { worlds: rows, totalBytes: total, quotaBytes, warnFraction: STO.WARN_FRACTION, fraction, warn: fraction >= STO.WARN_FRACTION };
}

/** Human-readable byte size (used by the read-out UI). */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
}
